import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

type PermissionsContextType = {
  isAdmin: boolean;
  permissions: {
    canManageUsers: boolean;
    canManageTransactions: boolean;
    canManageCategories: boolean;
    canViewAuditLogs: boolean;
    canManageSettings: boolean;
    canExportData: boolean;
    canDeleteData: boolean;
    canManageRoles: boolean;
  };
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  checkAdminAccess: () => Promise<void>;
};

const defaultPermissions = {
  canManageUsers: false,
  canManageTransactions: false,
  canManageCategories: false,
  canViewAuditLogs: false,
  canManageSettings: false,
  canExportData: false,
  canDeleteData: false,
  canManageRoles: false,
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [loading, setLoading] = useState(true);

  const checkAdminAccess = async () => {
    if (!user || !session) {
      setIsAdmin(false);
      setPermissions(defaultPermissions);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      console.log('🔍 Verificando acesso admin via Edge Function');
      console.log('👤 User ID:', user.id);
      console.log('📧 Email:', user.email);

      // Usar Edge Function para verificar acesso (bypassa RLS)
      const { data, error } = await supabase.functions.invoke('check-admin-access', {
        body: { userId: user.id }
      });

      console.log('📊 Resposta da Edge Function:', data);

      if (error) {
        console.error('❌ Erro ao chamar Edge Function:', error);
        setIsAdmin(false);
        setPermissions(defaultPermissions);
        setLoading(false);
        return;
      }

      if (data?.isAdmin) {
        console.log('✅ Admin encontrado!');
        console.log('📋 Dados do admin:', data.adminData);
        setIsAdmin(true);
        setPermissions({
          canManageUsers: true,
          canManageTransactions: true,
          canManageCategories: true,
          canViewAuditLogs: true,
          canManageSettings: true,
          canExportData: true,
          canDeleteData: true,
          canManageRoles: true,
        });
      } else {
        console.log('❌ Usuário não é admin');
        setIsAdmin(false);
        setPermissions(defaultPermissions);
      }
    } catch (error) {
      console.error('❌ Erro ao verificar permissões:', error);
      setIsAdmin(false);
      setPermissions(defaultPermissions);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    // Se for admin, tem todas as permissões
    if (isAdmin) {
      return true;
    }

    // Mapeamento de permissões
    const permissionMap: Record<string, boolean> = {
      'can_view_dashboard': true, // Todos podem ver o dashboard
      'can_manage_users': permissions.canManageUsers,
      'can_manage_transactions': permissions.canManageTransactions,
      'can_manage_categories': permissions.canManageCategories,
      'can_view_audit_logs': permissions.canViewAuditLogs,
      'can_manage_settings': permissions.canManageSettings,
      'can_export_data': permissions.canExportData,
      'can_delete_data': permissions.canDeleteData,
      'can_manage_roles': permissions.canManageRoles,
      'can_view_reports': true, // Todos podem ver relatórios
    };

    return permissionMap[permission] ?? false;
  };

  useEffect(() => {
    checkAdminAccess();
  }, [user, session]);

  return (
    <PermissionsContext.Provider value={{ isAdmin, permissions, loading, hasPermission, checkAdminAccess }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions deve ser usado dentro de um PermissionsProvider');
  }
  return context;
}
